import { useApolloClient } from '@apollo/client'
import {
  BoxLockVariationType,
  ChipKindEnum,
} from '@jusbrasil-web/shared-apollo-client/__generated__/types'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Get } from 'type-fest'

import { BoxLockQueryQuery, BoxLockQueryQueryVariables } from '../__generated__/query.graphql'
import { BoxLockQuery } from '../query'

// custom parameters, change it in dev and wait for Next refresh
const lockVariationType: BoxLockVariationType = BoxLockVariationType.UpdatePaymentInfoLock
const withCreditCard = true

// helper types
type BoxLockQueryBodyCards = Get<
  BoxLockQueryQuery,
  ['root', 'boxLockViewer', 'offerLockData', 'body', 'cards']
>
type BoxLockQueryBodyCardsCreditCard = Get<BoxLockQueryBodyCards, ['0', 'creditCard']>

// hook type
type UseQueryDataDebugProps = BoxLockQueryQueryVariables

export function useQueryDataDebug(props: UseQueryDataDebugProps) {
  const apolloClient = useApolloClient()
  const { appId, featureCode, fromComponent, boxOptions, pageMetadata, adminOptions } = props

  const queryData: BoxLockQueryQuery = useMemo(() => {
    const url = global.location?.href
    return {
      root: {
        __typename: 'RootApi',
        boxLockViewer: {
          __typename: 'BoxLock',
          lockVariationType,
          metadataToDebug: {
            userIntention: 'OTHER',
            experiments: [
              {
                experimentGroup: {
                  name: 'pricing',
                },
                alternative: {
                  name: 'default',
                },
                experiment: {
                  name: 'view-lr-content-limits',
                },
                participating: true,
              },
            ],
          },
          offerLockData: {
            __typename: 'OfferLockData',
            body: {
              __typename: 'OfferLockBody',
              cards: (
                [
                  {
                    __typename: 'OfferLockCard',
                    forceCheckout: false,
                    benefitsList: [
                      {
                        __typename: 'OfferLockBenefit',
                        name: 'Benefit 1',
                        isHighlighted: false,
                        icon: {
                          __typename: 'OfferLockIcon',
                          iconType: null,
                          color: '',
                        },
                      },
                      {
                        __typename: 'OfferLockBenefit',
                        name: 'Benefit 2',
                        isHighlighted: false,
                        icon: {
                          __typename: 'OfferLockIcon',
                          iconType: null,
                          color: '',
                        },
                      },
                    ],
                    banner: null,
                    chip: {
                      __typename: 'OfferLockChip' as const,
                      label: '',
                      kind: ChipKindEnum.Tertiary,
                    },
                    icon: { __typename: 'OfferLockIcon' as const, iconType: null, color: '' },
                    button: {
                      __typename: 'OfferLockButton',
                      label: 'Card button label',
                      url,
                      labelOnError: 'Card button label on error',
                    },
                    buttons: [
                      {
                        __typename: 'OfferLockButton',
                        label: 'Card button label',
                        kind: null,
                        url: '',
                      },
                    ],
                    creditCard: withCreditCard
                      ? ({
                          __typename: 'CreditCard',
                          brandName: 'visa',
                          ccToken: 'ccToken',
                          lastDigits: '1234',
                        } satisfies BoxLockQueryBodyCardsCreditCard)
                      : null,
                    descriptions: ['Card first description', 'Card second description'],
                    image: {
                      __typename: 'OfferLockImage',
                      alt: 'Card image alt',
                      src: 'https://static.jusbr.com/images/box-lock/inconsistentsvg.svg',
                    },
                    link: {
                      __typename: 'OfferLockLink',
                      label: 'Card link label',
                      url,
                    },
                    lockEventProperties: {
                      __typename: 'OfferLockEventProperties',
                      app_id: 'app_id',
                      current_plan: 'current_plan',
                      experiment_id: 'experiment_id',
                      feature: 'feature',
                      from_component: 'from_component',
                      modal_type: lockVariationType,
                      next_plan: 'next_plan',
                      pid: 123,
                      plan_price: 99.9,
                      resource_id: 'resource_id',
                      parent_resource_id: 'parent_resource_id',
                      is_upgrade: false,
                      is_one_click: false,
                    },
                    offer: {
                      __typename: 'OfferLockOffer',
                      priceTitle: 'Card offer price title',
                      priceText: 'Card offer price',
                      frequency: 'Card offer frequency',
                      currentPlanPriceText: 'Card offer current plan price',
                      priceAfterPromotionalPeriodText: 'Card offer price after promotional period',
                      frequencyPeriodText: 'Card offer frequency period',
                      installmentsText: null,
                      isRecommendedOffer: null,
                      priceTitlePlansSection: null,
                      presentationName: 'Premium',
                      frequencyInDays: 30,
                    },
                    submitData: {
                      __typename: 'OfferLockSubmitData',
                      jwt: '',
                      submitUrl: url,
                    },
                    subtitle: 'Card subtitle',
                    title: 'Card title',
                  },
                ] satisfies BoxLockQueryBodyCards
              ).flatMap((i) => [i, i]),
            },
            header: {
              __typename: 'OfferLockHeader',
              title: 'Header title',
              subtitle: 'Header subtitle',
              icon: { __typename: 'OfferLockIcon' as const, iconType: null, color: '' },
              toggle: {
                options: ['Mensal', 'Anual'],
                defaultOption: 'Mensal',
                description: {
                  text: 'Mock Description',
                  benefit: 'Mock Benefit',
                },
              },
            },
            footer: {
              __typename: 'OfferLockFooter',
              text: 'Footer text',
            },
          },
        },
        viewer: {
          __typename: 'Profile',
          currentPlan: {
            __typename: 'CurrentPlan',
            orderCode: null,
            isLoggedPlan: null,
          },
          currentOrganization: {
            __typename: 'Organization',
            owner: {
              __typename: 'Profile',
              subscriptions: [
                {
                  __typename: 'BoxSubscription',
                  subscriptionId: 3,
                  type: 'SUBSCRIPTION',
                  orderCode: 'orderCode',
                },
              ],
            },
          },
          pid: 123,
        },
      },
    } satisfies BoxLockQueryQuery
  }, [])

  const queryOptions = useMemo(
    () => ({
      query: BoxLockQuery,
      data: queryData,
      variables: {
        appId,
        featureCode,
        fromComponent,
        boxOptions,
        adminOptions,
        pageMetadata,
      },
    }),
    [appId, boxOptions, featureCode, fromComponent, queryData, pageMetadata, adminOptions]
  )

  const lastDataRef = useRef<BoxLockQueryQuery | null>(null)
  const [forceWriteCache, setForceWriteCache] = useState(0)

  useEffect(() => {
    apolloClient.writeQuery<BoxLockQueryQuery, BoxLockQueryQueryVariables>(queryOptions)
    lastDataRef.current = apolloClient.readQuery<BoxLockQueryQuery, BoxLockQueryQueryVariables>(
      queryOptions
    )
  }, [apolloClient, queryOptions, forceWriteCache])

  useEffect(() => {
    const subscription = apolloClient
      .watchQuery<BoxLockQueryQuery, BoxLockQueryQueryVariables>(queryOptions)
      .subscribe((observer) => {
        if (JSON.stringify(observer.data) !== JSON.stringify(lastDataRef.current)) {
          setForceWriteCache((prev) => prev + 1)
        }
      })

    return () => {
      subscription.unsubscribe()
    }
  }, [apolloClient, queryOptions])
}
